/**
 * useArcheHandoffIntake — the ONE sanctioned consumer of the `fiasAiPrefill`
 * navigation state (Arche Handoff, UI plane — see ../handoff.ts and
 * docs/arche-interop.md). An arche page that supports intake calls this hook;
 * reading `location.state.fiasAiPrefill` anywhere else is forbidden (pinned by
 * tests/architecture/arche-handoff-registry.test.ts).
 *
 * Behavior:
 * - Fires AT MOST ONCE per navigation: the state is cleared (history replace)
 *   before the handler runs, so refresh / back-forward never re-applies a
 *   handoff (a re-opened composer after every refresh would be user-hostile).
 * - Treats the payload as UNTRUSTED: any same-origin code can navigate with
 *   state. It re-validates with parseArcheHandoffPayload and checks the kind
 *   actually targets THIS arche; anything invalid is dropped loudly
 *   (breadcrumb) — never a crash, never a silent partial apply.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addBreadcrumb } from '@/lib/telemetry';
import {
  handoffTargetsArche,
  parseArcheHandoffPayload,
  type ArcheHandoffPayload,
} from '../handoff';

export function useArcheHandoffIntake(
  archeId: string,
  onHandoff: (payload: ArcheHandoffPayload) => void,
): void {
  const location = useLocation();
  const navigate = useNavigate();
  // The handler is read through a ref so a new closure each render doesn't
  // re-arm the effect (the location.key dep alone decides when to fire).
  const onHandoffRef = useRef(onHandoff);
  onHandoffRef.current = onHandoff;

  useEffect(() => {
    const raw = (location.state as { fiasAiPrefill?: unknown } | null)?.fiasAiPrefill;
    if (raw === undefined) return;
    // Consume-once: strip the state from history BEFORE applying, so a
    // mid-handler crash or refresh can't replay the handoff.
    navigate(location.pathname + location.search, { replace: true, state: null });
    const payload = parseArcheHandoffPayload(raw);
    if (!payload) {
      addBreadcrumb('arche-handoff', 'Dropped invalid handoff payload', { archeId });
      return;
    }
    if (!handoffTargetsArche(payload, archeId)) {
      addBreadcrumb('arche-handoff', 'Dropped mistargeted handoff payload', {
        archeId,
        kind: payload.kind,
      });
      return;
    }
    onHandoffRef.current(payload);
    // location.key identifies THIS history entry — a fresh navigation (new
    // key) re-arms the intake; re-renders of the same entry do not. The other
    // values used above are deliberately NOT deps: `onHandoff` is read through
    // a ref, and `location`/`navigate` change identity on every render, which
    // would re-fire the intake and re-apply the handoff.
    //
    // No eslint-disable here: this repo does not install
    // eslint-plugin-react-hooks, so a directive naming
    // `react-hooks/exhaustive-deps` is itself a lint ERROR ("Definition for
    // rule ... was not found") and fails the pre-push lint.
  }, [location.key]);
}
